import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, User, ShieldCheck, LineChart, FolderOpen, Sparkles,
  MessageSquare, DoorOpen, Settings, LogOut, Bookmark, Heart, Users, Briefcase,
  FileText, CreditCard, Bell, BarChart3, FileCog, Pin, ListTodo, Building2, Search, Coins, TrendingUp, Menu, Mail, BadgeCheck, GitCompare, FileSignature, Eye, ShieldAlert, X, Download, Landmark, Handshake, Settings2, ClipboardCheck, IndianRupee, ChevronDown, Inbox, Radio
} from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import { useBrand } from "../../lib/BrandContext";
import { useProperty } from "../../lib/PropertyContext";
import api from "../../lib/api";
import Seo from "../Seo";
import { Sheet, SheetContent } from "../ui/sheet";
import QuickCommand from "../QuickCommand";
import NotificationBell from "../NotificationBell";

const startupLinks = [
  { to: "/startup/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/startup/passport", label: "Startup Passport", icon: BadgeCheck },
  { to: "/startup/profile", label: "Profile", icon: User, section: "profile" },
  { to: "/startup/financial-profile", label: "Financial Profile", icon: LineChart },
  { to: "/startup/verification", label: "Verification", icon: ShieldCheck, section: "verification" },
  { to: "/startup/funding-profile", label: "Funding Profile", icon: LineChart, section: "funding" },
  { to: "/startup/documents", label: "Documents", icon: FolderOpen },
  { to: "/startup/matches", label: "Investor Matches", icon: Sparkles },
  { to: "/startup/investor-interest", label: "Investor Interest", icon: Heart, section: "interests" },
  { to: "/startup/queries", label: "Investor Questions", icon: MessageSquare, section: "queries" },
  { to: "/startup/deal-rooms", label: "Deal Rooms", icon: DoorOpen, section: "deal_rooms" },
  { to: "/my-debt-cases", label: "Debt Cases", icon: Landmark },
  { to: "/deal-velocity", label: "Deal Velocity", icon: TrendingUp },
  { to: "/esign", label: "E-Sign", icon: FileSignature, premium: true, section: "deal_rooms" },
  { to: "/startup/diligence-services", label: "Diligence Services", icon: Coins, premium: true, section: "diligence" },
  { to: "/startup/founder-premium", label: "Founder Premium", icon: BadgeCheck, premium: true },
  { to: "/wallet", label: "Wallet", icon: CreditCard },
  { to: "/messages", label: "Messages", icon: MessageSquare, section: "messages" },
  { to: "/startup/settings", label: "Settings", icon: Settings },
];

const investorLinks = [
  { to: "/investor/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/investor/profile", label: "Profile", icon: User, section: "profile" },
  { to: "/investor/preferences", label: "Preferences", icon: Sparkles },
  { to: "/investor/verification", label: "Verification", icon: ShieldCheck, section: "verification" },
  { to: "/investor/marketplace", label: "Curated Deals", icon: Briefcase },
  { to: "/investor/saved-startups", label: "Saved", icon: Bookmark },
  { to: "/investor/track-record", label: "Track Record", icon: TrendingUp },
  { to: "/investor/interests", label: "My Interests", icon: Heart, section: "interests" },
  { to: "/investor/queries", label: "Queries", icon: MessageSquare, section: "queries" },
  { to: "/investor/deal-rooms", label: "Deal Rooms", icon: DoorOpen, section: "deal_rooms" },
  { to: "/deal-velocity", label: "Deal Velocity", icon: TrendingUp },
  { to: "/investor/diligence-orders", label: "Diligence Orders", icon: Coins, premium: true, section: "diligence" },
  { to: "/esign", label: "E-Sign", icon: FileSignature, premium: true, section: "deal_rooms" },
  { to: "/wallet", label: "Wallet", icon: CreditCard },
  { to: "/investor/alert-preferences", label: "Alert Preferences", icon: Bell },
  { to: "/messages", label: "Messages", icon: MessageSquare, section: "messages" },
  { to: "/investor/settings", label: "Settings", icon: Settings },
];

const adminSections = [
  {
    label: "Approvals",
    property: "venturaz",
    links: [
      { to: "/admin/approvals", label: "Approvals Cockpit", icon: BadgeCheck },
      { to: "/admin/onboarding-requests", label: "Onboarding Requests", icon: BadgeCheck, section: "onboarding" },
      { to: "/admin/kyc-queue", label: "KYC Queue", icon: ShieldCheck, section: "kyc" },
      { to: "/admin/startups", label: "Startups", icon: Briefcase, section: "startups" },
      { to: "/admin/investors", label: "Investors", icon: Users, section: "investors" },
    ],
  },
  {
    label: "Diligence & Reviews",
    property: "venturaz",
    links: [
      { to: "/admin/company-intelligence", label: "Company Intelligence", icon: Building2 },
      { to: "/admin/financial-review", label: "Financial Review", icon: Coins },
      { to: "/admin/dd-reports", label: "Diligence Reports", icon: FileText },
      { to: "/admin/deep-dd", label: "Deep DD (Paid)", icon: ShieldCheck },
      { to: "/admin/coupons", label: "Coupons", icon: Coins },
      { to: "/admin/invoices", label: "Invoices", icon: FileText },
      { to: "/admin/billing-seller", label: "Invoice Seller", icon: Building2 },
      { to: "/admin/email-schedule", label: "Email Schedule", icon: Mail },
      { to: "/admin/daas-orders", label: "Diligence Orders", icon: Coins },
    ],
  },
  {
    label: "Deal Ops",
    property: "venturaz",
    links: [
      { to: "/admin/interests", label: "Interests", icon: Heart, section: "interests" },
      { to: "/admin/queries", label: "Queries", icon: MessageSquare, section: "queries" },
      { to: "/admin/deal-rooms", label: "Deal Rooms", icon: DoorOpen, section: "deal_rooms" },
      { to: "/admin/doc-change-requests", label: "Doc Change Queue", icon: FileSignature, section: "deal_rooms" },
      { to: "/admin/download-forensics", label: "Download Forensics", icon: Download },
      { to: "/esign", label: "E-Sign", icon: FileSignature, section: "deal_rooms" },
      { to: "/admin/match-overrides", label: "Match Overrides", icon: Pin },
      { to: "/admin/tasks", label: "Tasks & Escalations", icon: ListTodo },
      { to: "/messages", label: "Messages", icon: MessageSquare, section: "messages" },
    ],
  },
  {
    label: "VenturaZ Money",
    property: "venturaz_money",
    links: [
      { to: "/admin/debt", label: "Debt Cases", icon: Landmark },
      { to: "/admin/debt/leads", label: "Leads Inbox", icon: Inbox },
      { to: "/admin/debt/clients", label: "Clients (360)", icon: Users },
      { to: "/admin/lenders", label: "Lender Database", icon: Building2 },
      { to: "/admin/channel-partners", label: "Channel Partners", icon: Handshake },
      { to: "/admin/debt/config", label: "Debt Config (CMS)", icon: Settings2 },
      { to: "/admin/debt/notifications", label: "Debt Notifications", icon: Bell },
    ],
  },
  {
    label: "People & Payouts",
    // Property-neutral — same admins do payouts regardless of vertical.
    links: [
      { to: "/admin/roles-cms", label: "Roles CMS", icon: ShieldAlert, roles: ["super_admin", "admin"] },
      { to: "/admin/targets", label: "Employee Targets", icon: ClipboardCheck, roles: ["super_admin", "admin"] },
      { to: "/admin/incentive-rules", label: "Incentive Rules", icon: Coins, roles: ["super_admin", "admin"] },
      { to: "/admin/incentive-payouts", label: "Payout Ledger", icon: IndianRupee, roles: ["super_admin", "admin"] },
      { to: "/my-incentives", label: "My Incentives", icon: Coins },
    ],
  },
  {
    label: "Growth & Analytics",
    // Property-neutral for now — analytics roll up across both verticals.
    links: [
      { to: "/admin/analytics", label: "Insights", icon: BarChart3 },
      { to: "/admin/market-report", label: "Weekly Report", icon: TrendingUp },
      { to: "/admin/nurture-metrics", label: "Nurture Metrics", icon: Mail },
      { to: "/admin/mandate-graph", label: "Mandate Graph", icon: GitCompare },
      { to: "/admin/monitoring", label: "Monitoring & Nudges", icon: Bell },
      { to: "/admin/waitlist-analytics", label: "Waitlist Analytics", icon: Users },
      { to: "/admin/reports", label: "Reports", icon: BarChart3 },
    ],
  },
  {
    label: "Content & Admin",
    // Property-neutral. `/admin/user-property-access` is Super Admin-only.
    links: [
      { to: "/admin/users", label: "Users", icon: Users },
      { to: "/admin/user-property-access", label: "Property Access", icon: ShieldCheck, roles: ["super_admin"] },
      { to: "/admin/profile-requests", label: "Profile Requests", icon: BadgeCheck },
      { to: "/admin/plans", label: "Plans", icon: CreditCard },
      { to: "/admin/esign-pricing", label: "E-Sign Pricing", icon: FileSignature },
      { to: "/admin/daas-pricing", label: "DaaS Pricing", icon: Coins },
      { to: "/admin/cms", label: "CMS", icon: FileText },
      { to: "/admin/blogs", label: "Blog / Resources", icon: Sparkles },
      { to: "/admin/hero-pulse", label: "Hero Pulse", icon: Radio, section: "hero_pulse" },
      { to: "/admin/audit-logs", label: "Audit Logs", icon: FileCog },
      { to: "/admin/sessions", label: "Sessions Audit", icon: ShieldCheck },
      { to: "/admin/email-log", label: "Email Log", icon: Mail },
      { to: "/admin/notifications", label: "Notifications", icon: Bell },
      { to: "/admin/settings", label: "Settings", icon: Settings },
    ],
  },
];
// Flat list for legacy consumers (Quick Command palette etc.)
const adminLinks = adminSections.flatMap((s) => s.links);

const adminPreset = {
  sections: [
    { label: "Overview", links: [{ to: "/admin/dashboard", label: "Overview", icon: LayoutDashboard }] },
    ...adminSections,
  ],
  links: [
    { to: "/admin/dashboard", label: "Overview", icon: LayoutDashboard },
    ...adminLinks,
  ],
};

// Debt-only preset — the 4 pure-debt personas (debt_head / debt_manager /
// debt_agent / credit_analyst) see ONLY the VenturaZ Money sections. No
// equity screens, no KYC queue, no marketplace ops. They land on the debt
// Kanban board when they sign in.
const _debtOverviewSection = { label: "Overview", links: [{ to: "/admin/money/dashboard", label: "Money Overview", icon: LayoutDashboard }] };
const _venturazMoneySection = adminSections.find((s) => s.label === "VenturaZ Money");
const _peoplePayoutsSection = adminSections.find((s) => s.label === "People & Payouts");
const debtOnlySections = [
  _debtOverviewSection,
  _venturazMoneySection,
  // Payout Ledger + Roles CMS + Employee Targets stay admin-gated inside
  // People & Payouts via the per-link `roles:` filter. Debt personas will
  // only see "My Incentives" here.
  _peoplePayoutsSection,
].filter(Boolean);
const debtOnlyLinks = debtOnlySections.flatMap((s) => s.links);
const debtOnlyPreset = {
  sections: debtOnlySections,
  links: debtOnlyLinks,
};

const partnerLinks = [
  { to: "/partner/dashboard", label: "Partner Overview", icon: LayoutDashboard },
];

const rolePreset = {
  startup: { links: startupLinks, badge: "STARTUP · APPLICANT" },
  investor: { links: investorLinks, badge: "INVESTOR · CURATED" },
  channel_partner: { links: partnerLinks, badge: "CHANNEL PARTNER" },
  super_admin: { ...adminPreset, badge: "SUPER ADMIN" },
  admin: { ...adminPreset, badge: "ADMIN" },
  manager: { ...adminPreset, badge: "MANAGER" },
  agent: { ...adminPreset, badge: "AGENT" },
  debt_head: { ...debtOnlyPreset, badge: "DEBT HEAD · VZ MONEY" },
  debt_manager: { ...debtOnlyPreset, badge: "DEBT MANAGER · VZ MONEY" },
  debt_agent: { ...debtOnlyPreset, badge: "DEBT AGENT · VZ MONEY" },
  credit_analyst: { ...debtOnlyPreset, badge: "CREDIT ANALYST · VZ MONEY" },
};

function SidebarLink({ link, onNavigate, unreadCount }) {
  const Icon = link.icon;
  const premium = !!link.premium;
  const badge = Number(unreadCount) || 0;
  return (
    <NavLink
      to={link.to}
      onClick={onNavigate}
      data-testid={`side-${link.to.replace(/\//g, "-").slice(1)}`}
      className={({ isActive }) => {
        if (premium) {
          return `relative flex items-center gap-3 px-6 py-2.5 text-sm font-heading transition-all group ${
            isActive
              ? "text-[#FFD700] bg-gradient-to-r from-[#FFD700]/[0.14] to-transparent border-l-2 border-[#FFD700] shadow-[inset_0_0_20px_rgba(255,215,0,0.06)]"
              : "text-white/75 hover:text-[#FFD700] bg-gradient-to-r from-[#FFD700]/[0.05] to-transparent hover:from-[#FFD700]/[0.12] border-l-2 border-[#FFD700]/40 hover:border-[#FFD700]"
          }`;
        }
        // Nav items with unread notifications get a subtle gold-tinted left
        // border + a warmer text color so the eye is drawn to them without
        // needing the numeric badge to be visible on narrow mobile drawers.
        const attention = badge > 0;
        return `relative flex items-center gap-3 px-6 py-2.5 text-sm font-heading transition-colors ${
          isActive
            ? "text-[#FFD700] bg-white/[0.06] border-l-2 border-[#FFD700]"
            : attention
              ? "text-white hover:text-[#FFD700] bg-[#FFD700]/[0.04] border-l-2 border-[#FFD700]/60 hover:bg-[#FFD700]/[0.08]"
              : "text-white/60 hover:text-white hover:bg-white/[0.04] border-l-2 border-transparent"
        }`;
      }}
    >
      <Icon size={16} />
      <span className="flex-1">{link.label}</span>
      {badge > 0 && (
        <span
          className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#FFD700] text-[#0A1C40] text-[10px] font-mono font-semibold leading-none"
          data-testid={`side-badge-${link.to.replace(/\//g, "-").slice(1)}`}
          title={`${badge} new ${badge === 1 ? "update" : "updates"}`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
      {premium && badge === 0 && (
        <Sparkles size={11} className="text-[#FFD700]/80 group-hover:text-[#FFD700]" aria-hidden="true" />
      )}
    </NavLink>
  );
}

/**
 * SidebarContent — with collapsible sections and per-vertical accents.
 *
 * • Each section header (except the first "Overview" group) is clickable
 *   and collapses/expands its links. State is persisted in localStorage
 *   under key `venturaz.sidebar.collapsed` so the operator's layout
 *   preference survives a refresh.
 * • Sections in DEBT_SECTIONS get a copper accent + serif label to
 *   visually separate the Debt CRM from the equity side.
 */
const COLLAPSE_KEY = "venturaz.sidebar.collapsed.v1";
const DEBT_SECTIONS = new Set(["VenturaZ Money", "People & Payouts"]);

function loadCollapsed() {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_e) { return {}; }
}
function saveCollapsed(state) {
  try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(state)); } catch (_e) { /* noop */ }
}

function SidebarContent({ preset, user, logout, nav, onNavigate, kycState, sectionCounts = {} }) {
  const { logoUrl } = useBrand();
  const { properties, activeProperty, setActiveProperty } = useProperty();
  const isInternal = ["admin", "manager", "agent"].includes(user?.role);
  const showUnverified = isInternal && kycState && kycState.prompt_required;
  const canSee = (l) => !l.roles || l.roles.includes(user?.role);
  // A section is visible if:
  //   * it has no `property` tag (property-neutral), OR
  //   * its `property` matches the active property in the switcher, OR
  //   * there is no active property yet (still loading — show everything).
  const propertyAllows = (s) => {
    if (!s.property) return true;
    if (!activeProperty) return true;
    return s.property === activeProperty.slug;
  };
  // Rewrite the admin preset's "Overview" link to the Money Dashboard when
  // the active property is VenturaZ Money. Debt-only presets already point
  // to /admin/debt so this only affects super_admin/admin/manager/agent.
  const sectionsToRender = React.useMemo(() => {
    if (!preset.sections) return null;
    const onMoney = activeProperty?.slug === "venturaz_money";
    return preset.sections.map((s) => {
      if (s.label !== "Overview" || !onMoney) return s;
      return {
        ...s,
        links: s.links.map((l) =>
          l.to === "/admin/dashboard"
            ? { ...l, to: "/admin/money/dashboard", label: "Money Overview", icon: Landmark }
            : l
        ),
      };
    });
  }, [preset.sections, activeProperty?.slug]);
  const [collapsed, setCollapsed] = React.useState(loadCollapsed);
  const toggle = (key) => {
    setCollapsed((c) => {
      const next = { ...c, [key]: !c[key] };
      saveCollapsed(next);
      return next;
    });
  };
  const collapseAll = () => {
    const all = {};
    (sectionsToRender || preset.sections || []).forEach((s, i) => { if (i > 0 && s.label) all[s.label] = true; });
    setCollapsed(all); saveCollapsed(all);
  };
  const expandAll = () => { setCollapsed({}); saveCollapsed({}); };

  const linkFor = (l) => (
    <SidebarLink
      key={l.to}
      link={l}
      onNavigate={onNavigate}
      unreadCount={l.section ? sectionCounts[l.section] || 0 : 0}
    />
  );
  return (
    <>
      <div className="px-6 py-6 border-b border-white/10">
        {logoUrl ? (
          <img src={logoUrl} alt="VenturaZ" className="h-7 w-auto max-w-[160px] object-contain" data-testid="sidebar-brand-logo" />
        ) : (
          <div className="brand-mark brand-mark-inverted text-xl leading-none">
            <span className="word">VENTURAZ</span>
          </div>
        )}
        <div className="text-[9px] tracking-[0.28em] text-[#FFD700]/70 font-mono mt-1">POWERED BY CORPZO</div>
        <div className="mt-3 text-[10px] tracking-[0.3em] text-[#FFD700]/80 font-semibold">{preset.badge}</div>
      </div>
      {/* Property switcher — visible when the user has >1 property grant */}
      {properties && properties.length > 1 && (
        <div className="px-6 pt-4 pb-2 border-b border-white/10" data-testid="property-switcher-wrap">
          <div className="text-[9px] tracking-widest text-white/40 font-mono uppercase mb-1">Property</div>
          <select
            value={activeProperty?.slug || ""}
            onChange={(e) => setActiveProperty(e.target.value)}
            className="w-full text-sm bg-white/[0.05] border border-white/15 rounded px-2 py-1.5 text-white focus:outline-none focus:border-gold"
            data-testid="property-switcher-select"
          >
            {properties.map((p) => (
              <option key={p.slug} value={p.slug} className="text-ink">{p.name}</option>
            ))}
          </select>
        </div>
      )}
      {preset.sections && preset.sections.length > 1 && (
        <div className="px-6 pt-3 pb-1 flex items-center justify-between text-[9px] font-mono tracking-widest text-white/40 uppercase" data-testid="sidebar-collapse-tools">
          <span>Sections</span>
          <div className="flex gap-2">
            <button type="button" onClick={collapseAll} className="hover:text-white/80 transition-colors" data-testid="sidebar-collapse-all">Collapse all</button>
            <span className="text-white/20">·</span>
            <button type="button" onClick={expandAll} className="hover:text-white/80 transition-colors" data-testid="sidebar-expand-all">Expand all</button>
          </div>
        </div>
      )}
      <nav className="flex-1 py-2 overflow-y-auto" data-testid="sidebar-nav">
        {sectionsToRender ? (
          sectionsToRender.filter(propertyAllows).map((section, idx) => {
            const visible = section.links.filter(canSee);
            if (visible.length === 0) return null;
            const isDebt = DEBT_SECTIONS.has(section.label);
            // First section (usually Overview) has no header — never collapse.
            if (idx === 0 || !section.label) {
              return (
                <div key={section.label || `sec-${idx}`}>
                  {visible.map(linkFor)}
                </div>
              );
            }
            const isCollapsed = !!collapsed[section.label];
            return (
              <div key={section.label} className={`mt-4 ${isDebt ? "border-l-2 border-[#B8860B]/50 pl-0.5" : ""}`}>
                <button
                  type="button"
                  onClick={() => toggle(section.label)}
                  className={`w-full px-6 pb-2 pt-2 text-[9px] font-mono tracking-[0.28em] uppercase flex items-center justify-between transition-colors group ${
                    isDebt ? "text-[#F5C97A] hover:text-[#FFD98A]" : "text-white/40 hover:text-white/70"
                  }`}
                  aria-expanded={!isCollapsed}
                  data-testid={`sidebar-section-toggle-${section.label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <span className="flex items-center gap-1.5">
                    {isDebt && <span className="text-[#B8860B]">◆</span>}
                    {section.label}
                  </span>
                  <ChevronDown
                    size={11}
                    className={`transition-transform ${isCollapsed ? "-rotate-90" : ""}`}
                  />
                </button>
                {!isCollapsed && (
                  <div className={isDebt ? "sidebar-debt-links" : ""}>
                    {visible.map(linkFor)}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          preset.links.filter(canSee).map(linkFor)
        )}
      </nav>
      <div className="px-6 py-4 border-t border-white/10">
        <div className="text-xs text-white/50 mb-1">Signed in as</div>
        <div className="text-sm text-white font-heading truncate" data-testid="sidebar-user-email">{user?.email}</div>
        {showUnverified && (
          <div className="mt-2">
            <NavLink
              to="/settings/profile"
              onClick={onNavigate}
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-500/20 text-rose-200 border border-rose-500/40 text-[10px] font-mono tracking-wider hover:bg-rose-500/30 transition-colors"
              data-testid="sidebar-unverified-pill"
            >
              UNVERIFIED · KYC PENDING →
            </NavLink>
          </div>
        )}
        {kycState && kycState.status === "Verified" && (
          <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 text-[10px] font-mono tracking-wider">
            <ShieldCheck size={10} /> VERIFIED
          </div>
        )}
        <NavLink
          to="/settings/profile"
          onClick={onNavigate}
          className="mt-3 block text-left text-xs text-white/60 hover:text-[#FFD700] flex items-center gap-2 font-heading transition-colors"
          data-testid="sidebar-profile-link"
        >
          <User size={14} /> Profile settings
        </NavLink>
        <button
          onClick={async () => { await logout(); nav("/"); }}
          className="mt-2 w-full text-left text-xs text-white/60 hover:text-[#FFD700] flex items-center gap-2 font-heading transition-colors"
          data-testid="sidebar-logout-btn"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </>
  );
}

export default function DashboardShell({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const preset = rolePreset[user?.role] || rolePreset.startup;
  const [unread, setUnread] = useState(0);
  const [sectionCounts, setSectionCounts] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [kycState, setKycState] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchUnread = () => {
      api.get("/notifications/unread-count").then((r) => { if (!cancelled) setUnread(r.data?.count || 0); }).catch(() => {});
      // Per-section counts drive the sidebar badges.
      api.get("/notifications/unread-counts").then((r) => {
        if (!cancelled) setSectionCounts(r.data?.counts || {});
      }).catch(() => {});
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 45000); // Poll every 45s
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Fetch KYC state for sidebar pill + banner
  useEffect(() => {
    if (!user) return;
    api.get("/profile/me/kyc-state").then((r) => setKycState(r.data)).catch(() => {});
    setBannerDismissed(sessionStorage.getItem("vz_kyc_banner_dismissed") === "1");
  }, [user?.user_id]);

  // Auto-close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); setCmdOpen(false); }, [location.pathname]);

  // Cmd/Ctrl+K opens the Quick Command palette
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Swipe-from-left-edge to open mobile drawer (touch devices)
  useEffect(() => {
    let startX = null;
    let startY = null;
    let tracking = false;
    const EDGE = 24; // px from left edge to trigger
    const THRESH = 60; // px horizontal distance to open
    const V_TOLERANCE = 40; // px vertical drift allowed

    const onStart = (e) => {
      if (mobileOpen) return;
      // Only trigger on small screens where mobile menu is shown
      if (window.innerWidth >= 1024) return;
      const t = e.touches[0];
      if (t.clientX <= EDGE) {
        startX = t.clientX;
        startY = t.clientY;
        tracking = true;
      }
    };
    const onMove = (e) => {
      if (!tracking) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      if (dy > V_TOLERANCE) { tracking = false; return; }
      if (dx > THRESH) {
        tracking = false;
        setMobileOpen(true);
      }
    };
    const onEnd = () => { tracking = false; startX = null; startY = null; };

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [mobileOpen]);

  const notifPath = "/admin/notifications";
  const isAdmin = ["super_admin", "admin", "manager", "agent", "debt_head", "debt_manager", "debt_agent", "credit_analyst"].includes(user?.role);

  return (
    <div className="min-h-screen text-ink flex" data-testid="dashboard-shell">
      {/* Scoped CSS for internal Debt-CRM visual tint — copper accent on
          the Debt Marketplace + People & Payouts sidebar sections so
          internal debt users can scan quickly. */}
      <style>{`
        .sidebar-debt-links a { border-left: 2px solid transparent; }
        .sidebar-debt-links a:hover { border-left-color: #B8860B; background-color: rgba(184, 134, 11, 0.08); }
        .sidebar-debt-links a.active { border-left-color: #FFD700; background-color: rgba(184, 134, 11, 0.12); }
      `}</style><Seo
        title={title ? `${title} | VenturaZ` : "Dashboard | VenturaZ"}
        description={subtitle || "VenturaZ dashboard — manage your matches, deal rooms, documents and workflows."}
        noindex
      />
      {/* Desktop sidebar — liquid morph hero with a subtle scrim layer so nav
          labels stay legible when the drift blobs pass underneath.
          `.liquid-morph-hero` sets `position: relative` in index.css which
          would otherwise beat Tailwind's `sticky`, so we force position via
          inline style (highest specificity). */}
      <aside
        className="hidden lg:flex w-64 xl:w-72 flex-col liquid-morph-hero !rounded-none !border-0 border-r border-[#FFD700]/25 h-screen text-white shadow-[8px_0_24px_rgba(15,61,46,0.20)]"
        style={{ position: "sticky", top: 0 }}
        data-testid="sidebar"
      >
        {/* Scrim — sits above the drift blobs but below the SidebarContent so
            nav labels never dip below AA contrast. Non-interactive. */}
        <div
          className="absolute inset-0 pointer-events-none z-0"
          aria-hidden="true"
          style={{
            background: "linear-gradient(180deg, rgba(15,61,46,0.48) 0%, rgba(15,61,46,0.28) 40%, rgba(15,61,46,0.28) 60%, rgba(15,61,46,0.48) 100%)",
            backdropFilter: "blur(2px) saturate(115%)",
            WebkitBackdropFilter: "blur(2px) saturate(115%)",
          }}
        />
        <div className="relative z-10 flex-1 flex flex-col overflow-hidden">
          <SidebarContent preset={preset} user={user} logout={logout} nav={nav} kycState={kycState} sectionCounts={sectionCounts} />
        </div>
      </aside>

      {/* Mobile drawer — liquid morph via inner wrapper so the Sheet retains
          its own `position: fixed` from Tailwind (our .liquid-morph-hero rule
          declares `position: relative` which would otherwise fight it).
          NOTE: outer Sheet keeps an opaque navy base so the slide-in
          animation never composites the drift blobs against the moving page
          behind — that's what caused the flicker on mid-range mobile GPUs. */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="!bg-[#0F3D2E] !border-0 p-0 w-72 flex flex-col shadow-none"
          style={{
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)",
            willChange: "transform",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
          data-testid="mobile-sidebar"
        >
          <div className="liquid-morph-hero flex-1 flex flex-col border-r border-[#FFD700]/25 text-white overflow-hidden relative">
            {/* Scrim — same as desktop, keeps nav labels above AA when blobs pass under. */}
            <div
              className="absolute inset-0 pointer-events-none z-0"
              aria-hidden="true"
              style={{
                background: "linear-gradient(180deg, rgba(15,61,46,0.48) 0%, rgba(15,61,46,0.28) 40%, rgba(15,61,46,0.28) 60%, rgba(15,61,46,0.48) 100%)",
              }}
            />
            <div className="relative z-10 flex-1 flex flex-col overflow-hidden">
              <SidebarContent preset={preset} user={user} logout={logout} nav={nav} onNavigate={() => setMobileOpen(false)} kycState={kycState} sectionCounts={sectionCounts} />
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Quick command palette */}
      <QuickCommand open={cmdOpen} onOpenChange={setCmdOpen} />

      <main className="flex-1 min-w-0">
        <div className="relative z-40 px-4 sm:px-6 lg:px-10 py-4 lg:py-6 flex flex-wrap items-center justify-between gap-3 glass border-b-0" style={{ borderBottom: "1px solid rgba(15,61,46,0.06)" }}>
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 rounded-md hover:bg-navy/[0.04] text-ink-soft hover:text-ink transition-colors"
              aria-label="Open menu"
              data-testid="mobile-menu-toggle"
            >
              <Menu size={22} />
            </button>
            <div className="min-w-0">
              {subtitle && <div className="overline mb-1 truncate">{subtitle}</div>}
              <h1 className="font-heading text-xl sm:text-2xl md:text-3xl text-ink truncate" data-testid="page-title">{title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCmdOpen(true)}
              className="p-2 rounded-md hover:bg-navy/[0.04] text-ink-soft hover:text-ink transition-colors"
              title="Quick search (Ctrl/Cmd + K)"
              aria-label="Open quick search"
              data-testid="header-quick-search"
            >
              <Search size={18} />
            </button>
            {isAdmin && (
              <Link
                to="/admin/kyc-queue"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-md hover:bg-navy/[0.04] text-ink-soft hover:text-ink transition-colors text-xs font-heading"
                title="Approve pending KYC"
                data-testid="header-approve-kyc"
              >
                <ShieldCheck size={16} />
                <span>KYC</span>
              </Link>
            )}
            {notifPath && (
              <NotificationBell viewAllHref={isAdmin ? "/admin/notifications" : null} />
            )}
            {actions}
          </div>
        </div>
        <div className="p-4 sm:p-6 lg:p-10">
          {kycState?.prompt_required && !bannerDismissed && (
            <div className="mb-6 border border-rose-200 bg-rose-50 rounded-md p-4 flex items-start gap-3" data-testid="dashboard-kyc-banner">
              <ShieldAlert size={22} className="text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-rose-900 font-medium text-sm">Your VenturaZ identity is unverified</div>
                <div className="text-rose-800/80 text-xs mt-1">
                  Complete DigiLocker OKYC to unlock sensitive workflows. Ask a super_admin for an exemption if this doesn&apos;t apply to you.
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to="/settings/profile"
                  className="text-xs font-semibold px-3 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-500"
                  data-testid="dashboard-kyc-banner-cta"
                >
                  Verify now →
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    sessionStorage.setItem("vz_kyc_banner_dismissed", "1");
                    setBannerDismissed(true);
                  }}
                  className="text-rose-500 hover:text-rose-700 p-1"
                  aria-label="Dismiss"
                  data-testid="dashboard-kyc-banner-dismiss"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
